/* Supplier Onboarding — v3.9.2 (Jul 2026)
 *
 * Top-level Category-Management workbench for maintaining supplier-level
 * category discount contracts. Two supplier types share the same schema
 * and CRUD surface:
 *
 *   1. Hosted Catalog Suppliers  (brand/manufacturer in the UniBuy™
 *      30M-SKU hosted catalog — SKF, 3M, ABB, Grainger, …)
 *
 *   2. Sub-Supplier PunchOut      (cXML partners — Motion, Grainger,
 *      Fastenal, VWR, Lyreco, ZKH)
 *
 * For each (supplier × category) pair, admin captures two %:
 *   supplier_discount_pct   →  what the supplier gives Infosys off list
 *   customer_share_pct      →  fraction Infosys passes on to the buyer
 *
 * Category-lookup precedence when a punchout line comes back:
 *   1. Exact UNSPSC
 *   2. UNSPSC prefix (8 → 6 → 4 → 2 digits)
 *   3. Case-insensitive substring on Category Name
 *   4. Supplier default row
 *   5. Fallback → 0% ("Best price on UniBuy™" — positive framing)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Download,
  Plus,
  Trash2,
  Package,
  Building2,
  Loader2,
  FileSpreadsheet,
  Handshake,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/infoshop/admin/supplier-onboarding`;

function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `${Number(n).toFixed(2)}%`;
}

export default function SupplierOnboardingPage() {
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState("hosted"); // "hosted" | "sub_supplier"
  const [suppliers, setSuppliers] = useState({ hosted: [], sub_suppliers: [] });
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    unspsc: "",
    category_name: "",
    supplier_discount_pct: "",
    customer_share_pct: "",
    is_default: false,
    notes: "",
  });
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSession({
      network_id: params.get("network_id") || "INFOSYS.ADMIN",
      company_name: params.get("company_name") || "INFOSYS BPM",
      demo: params.get("demo") === "1" ? 1 : 0,
    });
  }, []);

  const authParams = useCallback(
    () => (session ? { network_id: session.network_id, company_name: session.company_name, demo: session.demo } : null),
    [session]
  );

  const loadSuppliers = useCallback(async () => {
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/suppliers`, { params: authParams() });
      setSuppliers({ hosted: data.hosted || [], sub_suppliers: data.sub_suppliers || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load suppliers");
    }
  }, [session, authParams]);

  const loadRules = useCallback(async () => {
    if (!session || !selectedSupplier) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/discounts`, {
        params: { ...authParams(), supplier_type: selectedSupplier.supplier_type, supplier_id: selectedSupplier.id },
      });
      setRules(data.rules || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, [session, selectedSupplier, authParams]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
  useEffect(() => { loadRules(); }, [loadRules]);

  const activeSuppliers = tab === "hosted" ? suppliers.hosted : suppliers.sub_suppliers;

  const filteredSuppliers = useMemo(
    () => activeSuppliers.filter((s) => s.supplier_type === tab),
    [activeSuppliers, tab]
  );

  const addRule = async () => {
    if (!selectedSupplier) return;
    if (!ruleForm.unspsc && !ruleForm.category_name && !ruleForm.is_default) {
      toast.error("Provide UNSPSC, Category Name, or mark as Default");
      return;
    }
    const sd = parseFloat(ruleForm.supplier_discount_pct) || 0;
    const cs = parseFloat(ruleForm.customer_share_pct) || 0;
    try {
      await axios.post(`${API}/discounts`, {
        supplier_type: selectedSupplier.supplier_type,
        supplier_id: selectedSupplier.id,
        supplier_name: selectedSupplier.name,
        unspsc: ruleForm.unspsc.trim(),
        category_name: ruleForm.category_name.trim(),
        supplier_discount_pct: sd,
        customer_share_pct: cs,
        is_default: ruleForm.is_default,
        notes: ruleForm.notes.trim(),
      }, { params: authParams() });
      toast.success("Rule saved");
      setRuleForm({ unspsc: "", category_name: "", supplier_discount_pct: "", customer_share_pct: "", is_default: false, notes: "" });
      loadRules();
      loadSuppliers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${API}/discounts/${id}`, { params: authParams() });
      toast.success("Rule deleted");
      loadRules();
      loadSuppliers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await axios.get(`${API}/discounts/template`, {
        params: authParams(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "discount-rules-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Template download failed");
    }
  };

  const submitBulk = async () => {
    if (!bulkFile || !selectedSupplier) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", bulkFile);
      form.append("supplier_type", selectedSupplier.supplier_type);
      form.append("supplier_id", selectedSupplier.id);
      form.append("supplier_name", selectedSupplier.name);
      form.append("network_id", session.network_id);
      form.append("company_name", session.company_name);
      form.append("demo", String(session.demo || 0));
      const { data } = await axios.post(`${API}/discounts/bulk-upload`, form);
      const errs = (data.errors || []).length;
      toast.success(`Bulk uploaded — ${data.inserted} new, ${data.updated} updated${errs ? `, ${errs} errors` : ""}`);
      if (errs) console.warn("[Supplier Onboarding] errors:", data.errors);
      setBulkFile(null);
      loadRules();
      loadSuppliers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  const effective =
    (parseFloat(ruleForm.supplier_discount_pct) || 0) *
    (parseFloat(ruleForm.customer_share_pct) || 0) /
    100.0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
          <Link to="/infoshop" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={16} />
            <span className="text-sm font-semibold">Back to UniBuy™</span>
          </Link>
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-md flex items-center justify-center text-white shadow-md"
              style={{ background: "linear-gradient(135deg, #0F766E 0%, #059669 100%)" }}
            >
              <Handshake size={17} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 font-bold">
                Infosys Aggregator · Category Mgmt
              </div>
              <div className="font-display font-bold text-slate-900 text-sm" data-testid="supplier-onboarding-title">
                Supplier Onboarding · Category Discount Rules
              </div>
            </div>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 lg:px-6 border-t border-slate-100">
          <nav className="flex gap-6">
            {[
              { k: "hosted", label: "Hosted Catalog Suppliers", icon: Building2, count: suppliers.hosted.length },
              { k: "sub_supplier", label: "Sub-Supplier PunchOut", icon: Package, count: suppliers.sub_suppliers.length },
            ].map((t) => {
              const I = t.icon;
              const active = tab === t.k;
              return (
                <button
                  key={t.k}
                  data-testid={`onboarding-tab-${t.k}`}
                  onClick={() => { setTab(t.k); setSelectedSupplier(null); setRules([]); }}
                  className={`inline-flex items-center gap-2 px-1 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  <I size={14} />
                  {t.label}
                  <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {t.count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left rail — supplier picker */}
        <aside className="lg:col-span-3">
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              {tab === "hosted" ? "Hosted brands" : "PunchOut partners"} ({filteredSuppliers.length})
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {filteredSuppliers.map((s) => (
                <button
                  key={s.id}
                  data-testid={`supplier-pick-${s.id}`}
                  onClick={() => setSelectedSupplier(s)}
                  className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 ${
                    selectedSupplier?.id === s.id ? "bg-emerald-50" : ""
                  }`}
                >
                  <div className="font-semibold text-sm text-slate-900 truncate">{s.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                    {tab === "hosted" ? (
                      <>SKUs: {s.sku_count} · Cats: {(s.categories || []).length}</>
                    ) : (
                      <>{(s.countries || []).join(", ") || "—"}</>
                    )}
                    <span className="ml-auto font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                      {s.discount_rule_count || 0} rules
                    </span>
                  </div>
                </button>
              ))}
              {filteredSuppliers.length === 0 && (
                <div className="p-4 text-xs text-slate-400 text-center">No suppliers yet.</div>
              )}
            </div>
          </div>
        </aside>

        {/* Right — detail */}
        <section className="lg:col-span-9">
          {!selectedSupplier && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-16 text-center">
              <Handshake size={32} className="mx-auto text-slate-300" />
              <div className="mt-3 font-display font-semibold text-slate-700">
                Select a supplier to manage its category-discount rules
              </div>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
                Each rule captures the supplier's category-level discount % and the % Infosys shares with the customer.
                Rules apply to matching PunchOut/hosted lines via UNSPSC or category-name lookup.
              </p>
            </div>
          )}

          {selectedSupplier && (
            <div className="space-y-5">
              <div className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                      {selectedSupplier.supplier_type === "hosted" ? "Hosted Catalog Supplier" : "Sub-Supplier PunchOut Partner"}
                    </div>
                    <div className="font-display text-2xl font-bold text-slate-900 mt-1">
                      {selectedSupplier.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {selectedSupplier.supplier_type === "hosted"
                        ? `${selectedSupplier.sku_count} hosted SKUs · Categories: ${(selectedSupplier.categories || []).slice(0, 4).join(", ") || "—"}`
                        : `Code: ${selectedSupplier.code} · Countries: ${(selectedSupplier.countries || []).join(", ") || "—"}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={downloadTemplate}
                      data-testid="onboarding-download-template"
                      variant="outline"
                      className="text-xs"
                    >
                      <FileSpreadsheet size={13} className="mr-2" /> Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* Rules table */}
              <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Active discount rules</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Match order: <span className="font-mono">UNSPSC exact → prefix → category name → default → 0%</span>
                    </div>
                  </div>
                  {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left">UNSPSC</th>
                        <th className="px-4 py-2 text-left">Category name</th>
                        <th className="px-4 py-2 text-right">Supplier %</th>
                        <th className="px-4 py-2 text-right">Customer share %</th>
                        <th className="px-4 py-2 text-right">Effective %</th>
                        <th className="px-4 py-2 text-left">Notes</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((r) => {
                        const eff = (r.supplier_discount_pct * r.customer_share_pct) / 100;
                        return (
                          <tr key={r.id} data-testid={`rule-row-${r.id}`} className="border-t border-slate-100">
                            <td className="px-4 py-2 font-mono text-[11px]">
                              {r.is_default ? <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">Default</span> : (r.unspsc || "—")}
                            </td>
                            <td className="px-4 py-2 text-slate-700">{r.category_name || "—"}</td>
                            <td className="px-4 py-2 text-right font-mono">{fmtPct(r.supplier_discount_pct)}</td>
                            <td className="px-4 py-2 text-right font-mono">{fmtPct(r.customer_share_pct)}</td>
                            <td className="px-4 py-2 text-right font-mono font-bold text-emerald-700">
                              {fmtPct(eff)}
                            </td>
                            <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-[16rem]">{r.notes || ""}</td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => deleteRule(r.id)}
                                data-testid={`rule-delete-${r.id}`}
                                className="text-rose-500 hover:text-rose-700"
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {rules.length === 0 && !loading && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-400">
                            No rules yet. Add one below or bulk-upload from Excel.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add rule form */}
              <div className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Add / update rule</div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <Input
                    value={ruleForm.unspsc}
                    onChange={(e) => setRuleForm({ ...ruleForm, unspsc: e.target.value })}
                    placeholder="UNSPSC (8-digit)"
                    data-testid="rule-unspsc"
                    className="h-9 text-sm md:col-span-1"
                  />
                  <Input
                    value={ruleForm.category_name}
                    onChange={(e) => setRuleForm({ ...ruleForm, category_name: e.target.value })}
                    placeholder="Category name"
                    data-testid="rule-category"
                    className="h-9 text-sm md:col-span-2"
                  />
                  <Input
                    value={ruleForm.supplier_discount_pct}
                    onChange={(e) => setRuleForm({ ...ruleForm, supplier_discount_pct: e.target.value })}
                    placeholder="Supplier %"
                    type="number"
                    data-testid="rule-supplier-pct"
                    className="h-9 text-sm"
                  />
                  <Input
                    value={ruleForm.customer_share_pct}
                    onChange={(e) => setRuleForm({ ...ruleForm, customer_share_pct: e.target.value })}
                    placeholder="Customer share %"
                    type="number"
                    data-testid="rule-customer-share"
                    className="h-9 text-sm"
                  />
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={ruleForm.is_default}
                      onChange={(e) => setRuleForm({ ...ruleForm, is_default: e.target.checked })}
                      data-testid="rule-is-default"
                    />
                    Default (fallback)
                  </label>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Input
                    value={ruleForm.notes}
                    onChange={(e) => setRuleForm({ ...ruleForm, notes: e.target.value })}
                    placeholder="Notes (optional)"
                    data-testid="rule-notes"
                    className="h-9 text-sm flex-1"
                  />
                  <div className="text-xs text-slate-500 font-mono whitespace-nowrap">
                    Effective: <span className="font-bold text-emerald-700">{effective.toFixed(2)}%</span>
                  </div>
                  <Button
                    onClick={addRule}
                    data-testid="rule-save-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                  >
                    <Plus size={13} className="mr-1" /> Save rule
                  </Button>
                </div>
              </div>

              {/* Bulk upload */}
              <div className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Bulk upload from Excel</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Use the template — headers must match. Existing rules with the same UNSPSC/category are updated in-place.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                    data-testid="onboarding-bulk-file"
                    className="text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300"
                  />
                  <Button
                    onClick={submitBulk}
                    disabled={!bulkFile || uploading}
                    data-testid="onboarding-bulk-submit"
                    className="bg-slate-900 hover:bg-slate-800 text-white text-xs"
                  >
                    {uploading ? <><Loader2 size={13} className="animate-spin mr-1" /> Uploading…</> : <><Upload size={13} className="mr-1" /> Upload</>}
                  </Button>
                  <Button
                    onClick={downloadTemplate}
                    variant="outline"
                    className="text-xs"
                  >
                    <Download size={13} className="mr-1" /> Template
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
